import { FormField, ModalDialog, QuestionMarkIcon, Select } from "@rothoblaas/design-system";
import classNames from "classnames";
import { memo, useEffect, useMemo, useState, type FC } from "react";
import { Controller, useFormContext, useFormState, useWatch } from "react-hook-form";
import FormFieldsContainer from "../../../components/ui/FormFieldsContainer";
import Icon from "../../../components/ui/Icon";
import SvgNoImage from "../../../components/ui/SvgNoImage";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import { DEFLECTION_OPTIONS, JOISTS_SPACING_OPTIONS } from "../../../http/deckDesigner/formulas";
import { Settings } from "../../../http/deckDesigner/types/settings";
import { SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM } from "../../../http/deckDesigner/types/substructureMaterials";
import type { SubstructureMaterialClassForSettings } from "../../../http/deckDesigner/types/substructureMaterialClasses";
import {
  isSubstructureAvailableFor,
  type SubstructureForSettings,
} from "../../../http/deckDesigner/types/substructures";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import { keepOnlyForCalculationWhileSelected } from "../../../utils/offeredSettingsItems";
import { getOptionImageSrc } from "./optionImage";
import { formatSubstructureLabel, formatSubstructureOptionLabel, useMeasurementFormat } from "./measurementFormat";
import OptionsStatus from "./OptionsStatus";
import {
  getSingleOptionAutoSelectSignature,
  useSingleOptionAutoSelect,
} from "../../../hooks/useSingleOptionAutoSelect";
import styles from "./SubstructureCard.module.css";
import { getNumberFormat } from "../../../utils/numberFormat";

const ERROR_FIELD_NAMES = ["joistsSpacing", "deflection"] as const;

type SubstructureCardProps = {
  defaultValues: DeckDesignerConfigurationFormValues;
  settings: Settings | undefined;
  hasError: boolean | undefined;
  hasVisitedAutoSelectTriangleState: (key: string) => boolean;
  rememberAutoSelectTriangleState: (key: string) => void;
  resetAutoSelectTriangleHistory: () => void;
};

type JoistsSpacingOption = (typeof JOISTS_SPACING_OPTIONS)[number];
type DeflectionOption = (typeof DEFLECTION_OPTIONS)[number];

const SubstructureCard: FC<SubstructureCardProps> = ({
  defaultValues,
  settings,
  hasError,
  hasVisitedAutoSelectTriangleState,
  rememberAutoSelectTriangleState,
  resetAutoSelectTriangleHistory,
}) => {
  const i18n = useI18n();
  const isReadOnly = useReadOnly();
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const joistsSpacingFormatter = getNumberFormat(i18n.translation.languageCode, { maximumFractionDigits: 2 });

  const { control, setValue } = useFormContext<DeckDesignerConfigurationFormValues>();
  // Scoped to this card's own fields: reading formState from the context subscribes the whole
  // form (useForm) instead, re-rendering every card on any validation.
  const { errors } = useFormState({ control, name: ERROR_FIELD_NAMES });

  const substructureMaterials = settings?.substructureMaterials;
  const substructureMaterialClasses = settings?.substructureMaterialClasses;

  const substructureMaterial = useWatch({ control, name: "substructureMaterial", exact: true });
  const substructureMaterialClass = useWatch({ control, name: "substructureMaterialClass", exact: true });
  const substructure = useWatch({ control, name: "substructure", exact: true });
  // One sent only because the calculation uses it stays only while selected (see
  // keepOnlyForCalculationWhileSelected).
  const substructureId = substructure?.id;
  const substructures = useMemo(
    () =>
      settings && keepOnlyForCalculationWhileSelected(settings.substructures, (option) => option.id === substructureId),
    [settings, substructureId],
  );
  const unitSystem = useWatch({ control, name: "unitSystem", exact: true });
  const measurementFormat = useMeasurementFormat();
  const isAluminiumSubstructureMaterial = substructureMaterial?.code === SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM;
  const { rememberSingleOptionChange, shouldAutoSelectSingleOption } = useSingleOptionAutoSelect();
  const [helpSubstructure, setHelpSubstructure] = useState<SubstructureForSettings | null>(null);
  const [isSubstructureSectionHelpOpen, setIsSubstructureSectionHelpOpen] = useState(false);

  const substructuresAvailableForMaterial = useMemo(
    () =>
      substructureMaterial
        ? substructures?.filter((option) =>
            option.substructureMaterials.some((m) => m.code === substructureMaterial.code),
          )
        : substructures,
    [substructureMaterial, substructures],
  );

  const visibleSubstructures = useMemo(
    () => substructuresAvailableForMaterial?.filter((option) => isSubstructureAvailableFor(option, unitSystem)),
    [substructuresAvailableForMaterial, unitSystem],
  );

  const substructureMaterialAutoSelectSignature = useMemo(
    () => getSingleOptionAutoSelectSignature(substructureMaterials, (option) => option.code),
    [substructureMaterials],
  );

  const substructureAutoSelectSignature = useMemo(
    () =>
      getSingleOptionAutoSelectSignature(
        visibleSubstructures,
        (option) => option.id,
        [substructureMaterial?.code, substructureMaterialClass?.id].join("|"),
      ),
    [substructureMaterial?.code, substructureMaterialClass?.id, visibleSubstructures],
  );

  useEffect(() => {
    const singleSubstructureMaterial = substructureMaterials?.length === 1 ? substructureMaterials[0] : undefined;

    if (
      singleSubstructureMaterial &&
      substructureMaterial?.code !== singleSubstructureMaterial.code &&
      shouldAutoSelectSingleOption("substructureMaterial", substructureMaterialAutoSelectSignature)
    ) {
      setValue("substructureMaterial", singleSubstructureMaterial);
    }
  }, [
    shouldAutoSelectSingleOption,
    setValue,
    substructureMaterial?.code,
    substructureMaterialAutoSelectSignature,
    substructureMaterials,
  ]);

  useEffect(() => {
    const singleVisibleSubstructure = visibleSubstructures?.length === 1 ? visibleSubstructures[0] : undefined;

    if (
      !singleVisibleSubstructure ||
      substructure?.id === singleVisibleSubstructure.id ||
      !shouldAutoSelectSingleOption("substructure", substructureAutoSelectSignature)
    ) {
      return;
    }

    const triangleKey = `substructure:${singleVisibleSubstructure.id}`;

    if (hasVisitedAutoSelectTriangleState(triangleKey)) {
      return;
    }

    rememberAutoSelectTriangleState(triangleKey);
    setValue("substructure", singleVisibleSubstructure);

    if (
      !substructureMaterial &&
      singleVisibleSubstructure.substructureMaterials.length === 1 &&
      shouldAutoSelectSingleOption("substructureMaterial", substructureMaterialAutoSelectSignature)
    ) {
      setValue("substructureMaterial", singleVisibleSubstructure.substructureMaterials[0] ?? null);
    }
  }, [
    hasVisitedAutoSelectTriangleState,
    rememberAutoSelectTriangleState,
    shouldAutoSelectSingleOption,
    setValue,
    substructure?.id,
    substructureAutoSelectSignature,
    substructureMaterial,
    substructureMaterialAutoSelectSignature,
    visibleSubstructures,
  ]);

  const getSubstructureLabel = (option: SubstructureForSettings): string =>
    formatSubstructureLabel(option, measurementFormat, i18n.translation.common);

  const getSubstructureOptionLabel = (option: SubstructureForSettings): string =>
    formatSubstructureOptionLabel(option, measurementFormat, i18n.translation.common);

  const filteredSubstructureMaterialClasses = useMemo(
    () =>
      substructureMaterial?.code
        ? substructureMaterialClasses?.filter(
            (option) => option.substructureMaterial.code === substructureMaterial?.code,
          )
        : substructureMaterialClasses,
    [substructureMaterialClasses, substructureMaterial?.code],
  );

  const substructureMaterialClassAutoSelectSignature = useMemo(
    () =>
      getSingleOptionAutoSelectSignature(
        filteredSubstructureMaterialClasses,
        (option) => option.id,
        substructureMaterial?.code,
      ),
    [filteredSubstructureMaterialClasses, substructureMaterial?.code],
  );

  const singleSubstructureMaterialClass =
    filteredSubstructureMaterialClasses?.length === 1 ? filteredSubstructureMaterialClasses[0] : undefined;

  const isSubstructureMaterialClassAutoSelected =
    !!singleSubstructureMaterialClass && substructureMaterialClass?.id === singleSubstructureMaterialClass.id;

  useEffect(() => {
    if (
      !singleSubstructureMaterialClass ||
      isSubstructureMaterialClassAutoSelected ||
      !shouldAutoSelectSingleOption("substructureMaterialClass", substructureMaterialClassAutoSelectSignature)
    ) {
      return;
    }

    setValue("substructureMaterialClass", singleSubstructureMaterialClass);
  }, [
    isSubstructureMaterialClassAutoSelected,
    shouldAutoSelectSingleOption,
    setValue,
    singleSubstructureMaterialClass,
    substructureMaterialClassAutoSelectSignature,
  ]);

  return (
    <div>
      <FormFieldsContainer fullWidth>
        <Controller
          name="substructureMaterial"
          control={control}
          defaultValue={defaultValues.substructureMaterial}
          render={({ field }) => (
            <FormField
              label={i18n.translation.deckDesignerModule.substructureMaterials.singular}
              warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
              info={
                substructureMaterials?.length === 1 && !!field.value
                  ? i18n.translation.common.singleOptionAvailable
                  : undefined
              }
            >
              <div className={styles.grid}>
                <OptionsStatus
                  className={styles.option}
                  isLoading={!hasError && !substructureMaterials}
                  hasError={!!hasError}
                />
                {substructureMaterials?.map((option) => {
                  const label = option.name;
                  const isSelected = field.value?.code === option.code;

                  return (
                    <button
                      key={option.code}
                      type="button"
                      className={classNames(styles.option, { [styles.selected]: isSelected })}
                      aria-pressed={isSelected}
                      onClick={() => {
                        if (isSelected) {
                          return;
                        }

                        resetAutoSelectTriangleHistory();
                        rememberSingleOptionChange(
                          "substructureMaterial",
                          substructureMaterialAutoSelectSignature,
                          option,
                        );
                        field.onChange(option);
                        // A material class belongs to one material, and the support depends on the class.
                        setValue("substructureMaterialClass", null);
                        setValue("support", null);
                        // The substructure stays if it's also available in the new material.
                        if (!substructure?.substructureMaterials.some((m) => m.code === option.code)) {
                          setValue("substructure", null);
                        }
                      }}
                    >
                      {option.image ? (
                        <img className={styles.image} src={getOptionImageSrc(option.image)} alt={label} />
                      ) : (
                        <SvgNoImage className={styles.image} aria-label={label} />
                      )}
                      <span className={styles.label}>{label}</span>
                    </button>
                  );
                })}
              </div>
            </FormField>
          )}
        />

        <Controller
          name="substructureMaterialClass"
          control={control}
          defaultValue={defaultValues.substructureMaterialClass}
          render={({ field }) => (
            <FormField
              label={i18n.translation.deckDesignerModule.substructureMaterialClasses.singular}
              warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
              info={isSubstructureMaterialClassAutoSelected ? i18n.translation.common.singleOptionAvailable : undefined}
            >
              {({ labelId, isInvalid }) => (
                <Select<SubstructureMaterialClassForSettings, false>
                  {...field}
                  inputId={labelId}
                  value={filteredSubstructureMaterialClasses?.find((option) => option.id === field.value?.id) ?? null}
                  options={filteredSubstructureMaterialClasses}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => `${option.name} - ${option.substructureMaterial.name}`}
                  placeholder={`${i18n.translation.common.select}...`}
                  isLoading={!substructureMaterialClasses && !hasError}
                  // A single option is auto-selected and shown disabled, like the other single-option fields.
                  isDisabled={
                    isReadOnly || !substructureMaterialClasses || !!hasError || isSubstructureMaterialClassAutoSelected
                  }
                  isClearable
                  isInvalid={isInvalid}
                  onChange={(selectedOption) => {
                    const nextMaterialClass = selectedOption ?? null;

                    resetAutoSelectTriangleHistory();
                    rememberSingleOptionChange(
                      "substructureMaterialClass",
                      substructureMaterialClassAutoSelectSignature,
                      nextMaterialClass,
                    );

                    if (!substructureMaterial) {
                      rememberSingleOptionChange(
                        "substructureMaterial",
                        substructureMaterialAutoSelectSignature,
                        nextMaterialClass?.substructureMaterial ?? null,
                      );
                      setValue("substructureMaterial", nextMaterialClass?.substructureMaterial ?? null);
                    }

                    setValue("support", null);
                    field.onChange(nextMaterialClass);
                  }}
                />
              )}
            </FormField>
          )}
        />

        <Controller
          name="substructure"
          control={control}
          defaultValue={defaultValues.substructure}
          render={({ field, fieldState }) => {
            const handleSubstructureChange = (nextSubstructure: SubstructureForSettings | null) => {
              resetAutoSelectTriangleHistory();
              rememberSingleOptionChange("substructure", substructureAutoSelectSignature, nextSubstructure);

              if (!substructureMaterial) {
                const nextSubstructureMaterial =
                  nextSubstructure?.substructureMaterials.length === 1
                    ? (nextSubstructure.substructureMaterials[0] ?? null)
                    : null;

                rememberSingleOptionChange(
                  "substructureMaterial",
                  substructureMaterialAutoSelectSignature,
                  nextSubstructureMaterial,
                );
                setValue("substructureMaterial", nextSubstructureMaterial);
              }

              setValue("support", null);
              field.onChange(nextSubstructure);
            };

            if (isAluminiumSubstructureMaterial) {
              return (
                <FormField
                  label={i18n.translation.deckDesignerModule.substructures.singular}
                  error={fieldState.error}
                  warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
                >
                  <div className={styles.grid}>
                    <OptionsStatus
                      className={styles.option}
                      isLoading={!hasError && !substructures}
                      hasError={!!hasError}
                    />
                    {visibleSubstructures?.map((option) => {
                      const label = getSubstructureOptionLabel(option);
                      const isSelected = field.value?.id === option.id;

                      return (
                        <div key={option.id} className={styles.optionWrapper}>
                          <button
                            type="button"
                            className={classNames(styles.option, { [styles.selected]: isSelected })}
                            aria-pressed={isSelected}
                            onClick={() => handleSubstructureChange(option)}
                          >
                            {option.image ? (
                              <img className={styles.image} src={getOptionImageSrc(option.image)} alt={label} />
                            ) : (
                              <SvgNoImage className={styles.image} aria-label={label} />
                            )}
                            <span className={styles.label}>{label}</span>
                          </button>
                          <button
                            type="button"
                            className={styles.help}
                            aria-label={i18n.translation.common.help}
                            onClick={(event) => {
                              event.stopPropagation();
                              setHelpSubstructure(option);
                            }}
                          >
                            <Icon glyph={QuestionMarkIcon} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </FormField>
              );
            }

            return (
              <FormField
                label={calculationTranslation.substructureSection(
                  measurementFormat.isImperial ? i18n.translation.common.unitInch : i18n.translation.common.unitMm,
                )}
                showHelp={{ onOpen: () => setIsSubstructureSectionHelpOpen(true) }}
                error={fieldState.error}
                warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
                info={
                  visibleSubstructures?.length === 1 && !!field.value
                    ? i18n.translation.common.singleOptionAvailable
                    : undefined
                }
              >
                {({ labelId, isInvalid }) => (
                  <Select<SubstructureForSettings, false>
                    {...field}
                    inputId={labelId}
                    value={visibleSubstructures?.find((option) => option.id === field.value?.id) ?? null}
                    options={visibleSubstructures}
                    getOptionValue={(option) => option.id}
                    getOptionLabel={getSubstructureLabel}
                    placeholder={`${i18n.translation.common.select}...`}
                    isLoading={!substructures && !hasError}
                    isDisabled={
                      isReadOnly ||
                      !substructures ||
                      !!hasError ||
                      (visibleSubstructures?.length === 1 && !!field.value)
                    }
                    isClearable
                    isInvalid={isInvalid}
                    onChange={(selectedOption) => handleSubstructureChange(selectedOption ?? null)}
                  />
                )}
              </FormField>
            );
          }}
        />

        <Controller
          name="joistsSpacing"
          control={control}
          defaultValue={defaultValues.joistsSpacing}
          rules={{
            validate: (value) => value == null || JOISTS_SPACING_OPTIONS.some((option) => option.value === value),
          }}
          render={({ field }) => (
            <FormField label={calculationTranslation.joistsSpacing} error={errors.joistsSpacing}>
              {({ labelId, isInvalid }) => (
                <Select<JoistsSpacingOption, false>
                  isDisabled={isReadOnly}
                  {...field}
                  inputId={labelId}
                  value={JOISTS_SPACING_OPTIONS.find((option) => option.value === field.value) ?? null}
                  options={JOISTS_SPACING_OPTIONS}
                  getOptionValue={(option) => `${option.value}`}
                  getOptionLabel={(option) =>
                    measurementFormat.length(
                      option.value,
                      "m",
                      `${joistsSpacingFormatter.format(option.value)} ${i18n.translation.common.unitM}`,
                    )
                  }
                  placeholder={`${i18n.translation.common.select}...`}
                  isClearable
                  isInvalid={isInvalid}
                  onChange={(selectedOption) => field.onChange(selectedOption?.value ?? null)}
                />
              )}
            </FormField>
          )}
        />

        <Controller
          name="deflection"
          control={control}
          defaultValue={defaultValues.deflection}
          rules={{
            validate: (value) => value == null || DEFLECTION_OPTIONS.some((option) => option.value === value),
          }}
          render={({ field }) => (
            <FormField label={calculationTranslation.deflection} error={errors.deflection}>
              {({ labelId, isInvalid }) => (
                <Select<DeflectionOption, false>
                  isDisabled={isReadOnly}
                  {...field}
                  inputId={labelId}
                  value={DEFLECTION_OPTIONS.find((option) => option.value === field.value) ?? null}
                  options={DEFLECTION_OPTIONS}
                  getOptionValue={(option) => `${option.value}`}
                  getOptionLabel={(option) => option.label}
                  placeholder={`${i18n.translation.common.select}...`}
                  isClearable
                  isInvalid={isInvalid}
                  onChange={(selectedOption) => field.onChange(selectedOption?.value ?? null)}
                />
              )}
            </FormField>
          )}
        />
      </FormFieldsContainer>

      {helpSubstructure && (
        <ModalDialog onClose={() => setHelpSubstructure(null)} fitContent>
          {helpSubstructure.image ? (
            <img
              className={styles.helpImage}
              src={getOptionImageSrc(helpSubstructure.image)}
              alt={getSubstructureLabel(helpSubstructure)}
            />
          ) : (
            <SvgNoImage className={styles.helpImage} aria-label={getSubstructureLabel(helpSubstructure)} />
          )}
          <p>{helpSubstructure.item?.description ?? getSubstructureLabel(helpSubstructure)}</p>
        </ModalDialog>
      )}

      {isSubstructureSectionHelpOpen && (
        <ModalDialog onClose={() => setIsSubstructureSectionHelpOpen(false)} fitContent>
          <img className={styles.helpImage} src="/deckDesigner/substructure-sizes.png" />
        </ModalDialog>
      )}
    </div>
  );
};

// Memoized: its props are stable, so it re-renders through its own watched fields and props only, not
// whenever its parent does.
export default memo(SubstructureCard);
